import { z } from "zod";

/**
 * The AI Storyteller's view of the world (docs/storyteller.md): `GET /v1/admin/storyteller/digest`
 * summarizes what players did in a window, the world's facts and chronicle, and the content ids a
 * proposal may use, so the feedback bot can ask a model for quests grounded in real play. Character
 * names only: no accounts, emails or ids of people.
 */

const Counts = z.record(z.string(), z.number());

export const StorytellerDeathSchema = z.object({
  at: z.string(),
  /** A creature id, or a cause ("drowning", "environment"). */
  by: z.string(),
  zone: z.string(),
  /** The nearest named place, or "the wilds". */
  near: z.string(),
});

export const StorytellerPlayerSchema = z.object({
  name: z.string(),
  level: z.number(),
  cls: z.string(),
  firstSeen: z.string(),
  lastSeen: z.string(),
  /**
   * Everything below is counted between `since` and `until`. `kills`: kills they fought in (the
   * game credits a quest only when they were alive and within 60 m, so it can be more than the
   * lifetime tally below).
   */
  kills: Counts,
  /** By resource node id (berry_bush, copper_vein…), not item. */
  gathered: Counts,
  felled: Counts,
  /** By item id, counting each recipe's output. */
  crafted: Counts,
  fished: Counts,
  built: Counts,
  /**
   * Their Shovel work by day and place (the game's `terraform.sum`, summed), the place named as the
   * HUD's compass names it with the nearest named spot ("The Meadows (by Hearthmoor, Hearthmoor
   * Plains)"); m³ raised and dug. Absent from a server before it: none.
   */
  reshaped: z
    .array(z.object({ day: z.string(), place: z.string(), raised: z.number(), dug: z.number() }))
    .default([]),
  deaths: z.array(StorytellerDeathSchema),
  questsCompleted: z.array(z.string()),
  duties: Counts,
  townPoints: z.number(),
  gifts: z.array(z.object({ npc: z.string(), item: z.string(), count: z.number() })),
  talkedTo: z.array(z.string()),
  zones: z.array(z.string()),
  museumDonations: z.number(),
  /**
   * Lifetime totals from the character's quest tally: what quest objectives count, since the tally
   * began (records from before it were seeded from finished quests, so it can be lower than this
   * week's counts). A proposal asking for less than this is instant for them.
   */
  lifetime: z.object({
    kills: Counts,
    /** By item id (what a gather objective counts). */
    gathered: Counts,
    crafted: Counts,
    built: Counts,
    visited: z.array(z.string()),
    talkedTo: z.array(z.string()),
    questsDone: z.number(),
  }),
});

export const StorytellerDigestSchema = z.object({
  /**
   * Every character name anywhere in this digest (players, holders, rare-beast witnesses, the
   * chronicle's people): the bot puts aliases in their place before a model or Discord sees it,
   * and drops this list (docs/storyteller.md "Names"). Null from a server older than it.
   */
  people: z.array(z.string()).nullable().default(null),
  /**
   * Whether new Storyteller quests can go live now: `tuning.storytellerEnabled`, and the cap on
   * unexpired ones (`tuning.storytellerQuestCap`) against how many there are. Null from an older
   * server.
   */
  publishing: z
    .object({ enabled: z.boolean(), cap: z.number(), live: z.number() })
    .nullable()
    .default(null),
  since: z.string(),
  until: z.string(),
  window: z.object({
    /** Game events read for this digest. */
    events: z.number(),
    /** More events than the digest reads were in the window (the counts are a lower bound). */
    truncated: z.boolean(),
    /** The oldest event still kept: a window starting before it lost its beginning. */
    oldestKept: z.string().nullable(),
  }),
  totals: z.object({
    activePlayers: z.number(),
    /** Characters that finished the first board quest between `since` and `until`. */
    newcomers: z.number(),
    kills: Counts,
    /** By resource node id. */
    gathered: Counts,
    felled: Counts,
    /** By item id. */
    crafted: Counts,
    fished: Counts,
    built: Counts,
    deathsBy: Counts,
    deathsNear: Counts,
    questsCompleted: Counts,
    duties: Counts,
    townPoints: z.number(),
    gifts: Counts,
    talks: Counts,
    travels: Counts,
    museumDonations: z.number(),
    lotsClaimed: z.number(),
    civicProjectsDone: z.number(),
  }),
  /** The most active players first (bounded). */
  players: z.array(StorytellerPlayerSchema),
  /**
   * Rare beasts seen in the window (docs/rare-beasts.md): how often one rose, who saw it, who
   * killed it and where. Rumours for quests ("a kelpie at the delta…").
   */
  rareBeasts: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        tier: z.enum(["uncommon", "rare", "legendary"]),
        risen: z.number(),
        sightings: z.number(),
        seenBy: z.array(z.string()),
        kills: z.array(
          z.object({ at: z.string(), by: z.array(z.string()), place: z.string().nullable() }),
        ),
        lastSeen: z.object({ at: z.string(), place: z.string().nullable() }).nullable(),
      }),
    )
    .default([]),
  /**
   * Every other thing players did (docs/storyteller.md "Activity"): each event kind but noise, by
   * kind, with how many and how many players (exact), and its fields summarized from the events
   * read: a text field's top values, a number's sum and max (`build`'s `h`: height above ground).
   * Kinds the totals already detail carry only their number fields. Empty from an older server.
   */
  activity: z
    .record(
      z.string(),
      z.object({
        n: z.number(),
        players: z.number(),
        fields: z
          .record(
            z.string(),
            z.object({
              top: Counts.optional(),
              sum: z.number().optional(),
              max: z.number().optional(),
            }),
          )
          .optional(),
      }),
    )
    .default({}),
  /**
   * What stands out in the window (docs/storyteller.md "Highlights"), ranked, at most 20: records
   * (the biggest number a field has ever reached), firsts (a kind's first ever), spikes (a kind
   * far above its usual daily count) and values never seen before. "Ever" means since `since`,
   * when the server began keeping track. Empty from an older server.
   */
  highlights: z
    .object({
      since: z.string().nullable(),
      items: z.array(z.object({ day: z.string(), text: z.string() })),
    })
    .default({ since: null, items: [] }),
  /**
   * How the world changed (docs/storyteller.md "World changes"), in short lines: structures new,
   * grown and gone (pieces clustered: size, height above ground, piece mix, place, whose), the
   * tallest and largest standing, ground reshaped, the town storages' and Town Points' changes,
   * and the busiest places. `since`: the daily snapshot compared with (null: none yet). Empty from
   * an older server.
   */
  worldChanges: z
    .object({ since: z.string().nullable(), lines: z.array(z.string()) })
    .default({ since: null, lines: [] }),
  /**
   * What players tried and the game refused (`reject` events), by action and reason (numbers
   * folded to N), biggest first: an ambition the game blocked is a story. Empty from an older
   * server.
   */
  rejects: z
    .array(
      z.object({
        action: z.string(),
        reason: z.string(),
        n: z.number(),
        players: z.number(),
        /** The highest a refused build reached for (metres above ground). */
        h: z.number().optional(),
      }),
    )
    .default([]),
  world: z.object({
    facts: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
    chronicle: z.array(z.object({ at: z.string(), text: z.string() })),
    permits: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        holder: z.string().nullable(),
        points: z.number(),
      }),
    ),
    townPoints: z.number(),
    /** The town's communal goals (open and recently done): what a new one should not repeat. */
    communal: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          storyteller: z.boolean(),
          /** Share of the goal met, 0..1. */
          progress: z.number(),
          done: z.boolean(),
          contributors: z.number(),
        }),
      )
      .default([]),
    /** Bounties (claimed quests): who holds each, whether it is done. */
    bounties: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          holder: z.string().nullable(),
          done: z.boolean(),
        }),
      )
      .default([]),
  }),
  /** What exists today: a proposal may only use these ids. */
  content: z.object({
    levelCap: z.number(),
    /** The server's XP cap for a quest of each level (index level − 1); absent from older servers. */
    xpCapByLevel: z.array(z.number()).default([]),
    /** Bag slots: a give objective must fit in them (count <= slots x stack). */
    inventorySlots: z.number(),
    objectiveKinds: z.array(z.string()),
    zones: z.array(z.object({ id: z.string(), name: z.string() })),
    /** The home zone (where new players start, and the town is): its id, name and town's name. */
    home: z
      .object({ id: z.string(), name: z.string(), town: z.string() })
      .default({ id: "island", name: "The Island", town: "Hearthmoor" }),
    /** Days an approved quest stays on the board when its proposal doesn't say, by scope. */
    questLifetimeDays: z
      .object({
        individual: z.number(),
        claimed: z.number(),
        communal: z.number(),
        role: z.number(),
      })
      .default({ individual: 7, claimed: 7, communal: 7, role: 14 }),
    creatures: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        level: z.number(),
        zones: z.array(z.string()),
        /** Where it spawns (world-frame metres; a few per creature). */
        spawns: z.array(z.object({ zone: z.string(), x: z.number(), z: z.number() })),
        nightOnly: z.boolean(),
        /** Comes in the night raids on the town (docs/town-world.md), not from spawn points. */
        raid: z.boolean(),
        /**
         * A rare beast (docs/rare-beasts.md): appears only when `conditions` hold, out of sight;
         * its `hint` is what players' Bestiaries say of it before anyone kills one.
         */
        rare: z
          .object({
            tier: z.enum(["uncommon", "rare", "legendary"]),
            hint: z.string(),
            conditions: z.string(),
          })
          .optional(),
      }),
    ),
    /** `sources`: how a player can get it today (node, tree, loot, recipe, fish); empty = none. */
    items: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        rarity: z.string(),
        /** Most in one bag slot (1 = unstackable). */
        stack: z.number(),
        consumable: z.boolean(),
        sources: z.array(z.string()),
      }),
    ),
    recipes: z.array(
      z.object({ id: z.string(), item: z.string(), station: z.string().nullable() }),
    ),
    /** Players build only inside a settler lot (their own or a public one); a town piece needs its permit. */
    pieces: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        /** A lamp: lit, it keeps light-fearing creatures (the night raiders) away. */
        lamp: z.boolean(),
        /** Only the holder of this permit builds it (in town); null: anyone, in a lot. */
        townPermit: z.string().nullable(),
      }),
    ),
    npcs: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        title: z.string().nullable(),
        zone: z.string(),
        /** Quests this NPC gives today. */
        gives: z.number(),
        /** In the world right now (an NPC with a `when` is only there while it holds). */
        present: z.boolean(),
        conditional: z.boolean(),
      }),
    ),
    pois: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        kind: z.string(),
        zone: z.string(),
        x: z.number(),
        z: z.number(),
      }),
    ),
    quests: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        level: z.number(),
        xp: z.number(),
        giver: z.string(),
        objectives: z.string(),
        source: z.string().default("authored"),
        scope: z.string().default("individual"),
        timing: z.string().default("untimed"),
        /** Storyteller quests: when they leave the board (ISO). */
        expires: z.string().nullable().default(null),
      }),
    ),
    permits: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        blurb: z.string().default(""),
        /**
         * The permit's daily duties from the next 4:00 reset: its newest unexpired Storyteller
         * dailies, or (with none) its authored ones. `slots`: how many Storyteller dailies it uses.
         */
        dailies: z
          .array(
            z.object({
              id: z.string(),
              name: z.string(),
              objective: z.string(),
              townPoints: z.number(),
              source: z.string(),
              expires: z.string().nullable(),
            }),
          )
          .default([]),
        slots: z.number().default(1),
      }),
    ),
    /** When `permits[].dailies` start: the next 4:00 reset (ISO; null from an older server). */
    dailiesFrom: z.string().nullable().default(null),
    /** What a permit's daily duty may count: the town storages a delivery goes to, the deeds. */
    duties: z
      .object({ storages: z.array(z.string()), deeds: z.array(z.string()) })
      .default({ storages: [], deeds: [] }),
  }),
});
export type StorytellerDigest = z.infer<typeof StorytellerDigestSchema>;
export type StorytellerPlayer = z.infer<typeof StorytellerPlayerSchema>;
